import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { anesthesiaApi } from '../api/anesthesiaApi';
import { mapBackendToForm, mapFormToBackend } from '../utils/mapping';
import { useNotification } from '../../../../contexts/NotificationContext';
import { clearDraft, DRAFT_KEYS, loadDraft } from '../../../../utils/draftUtils';

export const useAnesthesiaForm = (uhid, navigate) => {
  const { showNotification } = useNotification();
  const signaturePadRef = useRef(null);

  // Grids
  const drugGridRef = useRef();
  const primaryMonitoringRef = useRef();
  const advancedMonitoringRef = useRef();

  const [formData, setFormData] = useState({
    uhid_no: uhid || "",
    pulse: "",
    bp: "",
    others: "",
    signName: "",
    signDateTime: "",
    signature: "",
    anaesthetist1: "",
    anaesthetist2: "",
    surgeon1: "",
    surgeon2: "",
    oralNasal: "",
    cuffed: "",
    size: "",
    scopeGrade: "",
    inOut: "",
    ivAccess: "",
    preOxygenation: "",
    rapidSequence: "",
    inhalationalAgent: "",
    throatPack: "",
    monitoring: {
      spo2: false, ecg: false, nibp: false, etco2: false, fio2: false, temp: false, cvp: false, arterialLine: false, uop: false,
    },
    equipmentChecked: false,
    consentChecked: false,
    drugInputs: ["", "", "", "", "", "", "", ""],
    n2o: "",
    oxygenF1Fe: "",
    vapourF1Fe: "",
    ecgPosition: "",
    temperatureReading: "",
    ivFluid: "",
    bloodLoss: "",
    urineOutput: "",
    tourniquet: "",
    procedures: {
      drugReversal: false, postOperative: false, postOpWard: false, delayedRecovery: false, drugReaction: false,
      aspiration: false, cardiacArrest: false, others: false,
    },
    clinicalNotes: "",
    drugChartData: "",
    primaryMonitoringData: "",
    advancedMonitoringData: "",
  });

  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [hasDataFinishedLoading, setHasDataFinishedLoading] = useState(false);

  useEffect(() => {
    const draft = loadDraft(DRAFT_KEYS.ANESTHESIA_FORM, uhid);
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
    }
  }, [uhid]);

  const { data: fetchedEvalData, isLoading: isInitialLoading } = useQuery({
    queryKey: ['anesthesiaForm', uhid],
    queryFn: async () => {
      try {
        const evalRes = await anesthesiaApi.getEval(uhid);
        return evalRes;
      } catch (e) {
        return null;
      }
    },
    enabled: !!uhid,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // Treat undefined as loading, null as no data, and object as data
    if (fetchedEvalData === undefined) return;
    
    setHasDataFinishedLoading(true);
    
    if (fetchedEvalData && Object.keys(fetchedEvalData).length > 0) {
      setIsUpdate(true);
      const mappedData = mapBackendToForm(fetchedEvalData, uhid);
      
      setFormData(prev => ({
        ...prev,
        ...mappedData,
        uhid_no: uhid,
      }));

      // Need to populate the refs, so giving a small delay back to the component
      setTimeout(() => {
        if (mappedData.drugChartData && drugGridRef.current) {
          try { drugGridRef.current.setGridData(JSON.parse(mappedData.drugChartData)); } catch(e){}
        }
        if (mappedData.primaryMonitoringData && primaryMonitoringRef.current) {
          try { primaryMonitoringRef.current.setGridData(JSON.parse(mappedData.primaryMonitoringData)); } catch(e){}
        }
        if (mappedData.advancedMonitoringData && advancedMonitoringRef.current) {
          try { advancedMonitoringRef.current.setGridData(JSON.parse(mappedData.advancedMonitoringData)); } catch(e){}
        }
      }, 200);
    } else {
      setIsUpdate(false);
    }
  }, [fetchedEvalData, uhid]);

  const submitMutation = useMutation({
    mutationFn: (data) => {
      const gridDataObj = collectGridData();
      const payload = mapFormToBackend(data, gridDataObj);
      if (isUpdate) {
        return anesthesiaApi.updateEval(uhid, payload);
      }
      return anesthesiaApi.createEval(payload);
    },
    onSuccess: () => {
      showNotification('success', isUpdate ? 'Anesthesia Form updated successfully!' : 'Anesthesia Form submitted successfully!');
      clearDraft(DRAFT_KEYS.ANESTHESIA_FORM, uhid);
      navigate(`/mainPage/${uhid}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    },
    onError: (error) => {
        showNotification('error', `Submission Failed: ${error.response?.data?.message || error.message}`);
    }
  });

  const collectGridData = () => {
    return {
      drugChartData: JSON.stringify(drugGridRef.current?.getGridData() || []),
      primaryMonitoringData: JSON.stringify(primaryMonitoringRef.current?.getGridData() || []),
      advancedMonitoringData: JSON.stringify(advancedMonitoringRef.current?.getGridData() || []),
    };
  };

  const handleChange = useCallback((field) => (e) => {
    let value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleNameChange = useCallback((e) => {
    const name = e.target.value;
    const now = new Date();
    const dateTimeString = now.toISOString().slice(0, 16);

    setFormData(prev => ({
      ...prev,
      signName: name,
      signDateTime: dateTimeString
    }));
  }, []);

  const handleMonitoringChange = useCallback((field) => (e) => {
    setFormData(prev => ({
      ...prev,
      monitoring: {
        ...prev.monitoring,
        [field]: e.target.checked
      }
    }));
  }, []);

  const handleProcedureChange = useCallback((field) => (e) => {
    setFormData(prev => ({
      ...prev,
      procedures: {
        ...prev.procedures,
        [field]: e.target.checked
      }
    }));
  }, []);

  const handleDrugInputChange = useCallback((index, value) => {
    const newDrugInputs = [...formData.drugInputs];
    newDrugInputs[index] = value;
    setFormData(prev => ({
      ...prev,
      drugInputs: newDrugInputs
    }));
  }, [formData.drugInputs]);

  const handleSignatureOpen = () => {
    setSignatureDialogOpen(true);
  };

  const handleSignatureSave = () => {
    if (signaturePadRef.current) {
        const canvas = signaturePadRef.current.getCanvas();
        if(canvas) {
            const signatureDataURL = canvas.toDataURL();
            setFormData(prev => ({ ...prev, signature: signatureDataURL }));
        }
    }
    setSignatureDialogOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitMutation.mutate(formData);
  };

  return {
    formData,
    setFormData,
    loading: isInitialLoading || submitMutation.isLoading || !hasDataFinishedLoading,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    drugGridRef,
    primaryMonitoringRef,
    advancedMonitoringRef,
    isUpdate,
    handleChange,
    handleNameChange,
    handleMonitoringChange,
    handleProcedureChange,
    handleDrugInputChange,
    handleSignatureOpen,
    handleSignatureSave,
    handleSubmit,
  };
};
